<template>
  <div class="preika-dashboard">
    <!--
      ログイン画面でパスキーやGoogleを試して入れなかった人に、
      **入った直後、この場で登録させる。**

      押した時点では作れない（誰のアカウントか決まっていないため）。
      あの画面で案内を読んでも、メールとパスワードを入れているうちに
      忘れられる。「押したのに設定できない」という声はそこから来ている。
      案内ではなく、登録そのものをここで終わらせる。
    -->
    <SignInMethodSetupDialog/>

    <section class="hero-panel">
      <div class="hero-panel__glow hero-panel__glow--one"></div>
      <div class="hero-panel__glow hero-panel__glow--two"></div>

      <div class="hero-panel__content">
        <div class="hero-brand">
          <img
            src="/img/avatars/preika_title.png"
            :alt="productName"
            class="hero-brand__logo"
          >
          <!--
            **英語の飾りをやめた。**WORKPLACE PLATFORM は言い過ぎで、
            給与など別の製品を出したときに、どちらも同じ名乗りになる。
            何ができるかを、そのまま書く。
          -->
          <!--
            **メニューに実際にある名前だけを書く。**「日報」と書いていたが、
            そんな画面は無い（あるのは「業務報告」）。確かめずに置いた。
          -->
          <span class="hero-brand__badge">申請・経費・業務報告</span>
        </div>

        <!--
          日本語で書く。Good morning を直に置いていて、しかも氏名が
          呼び捨てだった。毎朝、全員が見るところ。
        -->
        <p class="hero-panel__eyebrow">{{ greeting }}</p>
        <h1 class="hero-panel__title">
          今日の業務を、<br>
          <span>ひとつの画面から。</span>
        </h1>
        <!--
          **メニューにある名前で書く。**「月次報告」という画面は無い。
          あるのは「業務報告」と、事務・管理向けの「月次集計」。
          言い換え（スムーズに・シンプルに）を重ねても、何ができるかは
          増えない。することを、そのまま書く。
        -->
        <p class="hero-panel__description">
          勤務表と業務報告、休暇の申請から経費の精算まで。
          今日やることを{{ productName }}にまとめています。
        </p>

        <div class="hero-panel__meta">
          <div class="hero-meta-item">
            <CIcon name="cil-calendar"/>
            <span>{{ formattedDate }}</span>
          </div>
          <div class="hero-meta-item">
            <CIcon name="cil-clock"/>
            <span>{{ formattedTime }}</span>
          </div>
        </div>
      </div>

      <div class="hero-visual" aria-hidden="true">
        <div class="hero-visual__orbit hero-visual__orbit--one"></div>
        <div class="hero-visual__orbit hero-visual__orbit--two"></div>
        <div class="hero-visual__card hero-visual__card--rear">
          <span class="visual-line visual-line--short"></span>
          <span class="visual-line"></span>
          <span class="visual-line visual-line--middle"></span>
        </div>
        <div class="hero-visual__card hero-visual__card--front">
          <div class="visual-card__header">
            <span class="visual-avatar">P</span>
            <span class="visual-status"></span>
          </div>
          <div class="visual-chart">
            <span style="height: 36%"></span>
            <span style="height: 58%"></span>
            <span style="height: 45%"></span>
            <span style="height: 78%"></span>
            <span style="height: 64%"></span>
            <span style="height: 92%"></span>
          </div>
          <div class="visual-card__footer">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    </section>

    <!--
      **右のカード（よく使うメニュー）が出ないときは、1列にする。**
      2列のままだと、打刻のカードの右が空いたままになる。
    -->
    <section class="dashboard-grid dashboard-grid--top" :class="{ 'dashboard-grid--single': !quickMenu.length }">
      <article class="dashboard-card attendance-card">
        <div class="card-heading">
          <div>
            <p class="card-heading__eyebrow">ATTENDANCE</p>
            <h2>打刻</h2>
          </div>
          <span
            class="status-pill"
            :class="{
              'status-pill--active': attendanceStatus === 'WORKING' && !stampingStatusError,
               'status-pill--break': attendanceStatus === 'BREAK' && !stampingStatusError,
               'status-pill--scheduled': attendanceStatus === 'SCHEDULED' && !stampingStatusError,
              'status-pill--error': stampingStatusError
            }"
          >
            <span class="status-pill__dot"></span>
            {{ attendanceStatusLabel }}
          </span>
        </div>

        <div class="attendance-time">
          <strong>{{ formattedTime }}</strong>
          <span>{{ formattedDateWithWeekday }}</span>
        </div>

        <div class="attendance-actions">
          <button
            type="button"
            class="attendance-button attendance-button--start"
            :disabled="loading || stampingStatusLoading || !canStartAttendance"
            @click="handleTime('start')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-media-play"/></span>
            <span><strong>出勤</strong><small>業務を開始する</small></span>
          </button>

          <button
            type="button"
            class="attendance-button attendance-button--end"
            :disabled="loading || stampingStatusLoading || !canEndAttendance"
            @click="handleTime('end')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-media-stop"/></span>
            <span><strong>退勤</strong><small>業務を終了する</small></span>
          </button>

          <button
            type="button"
            class="attendance-button attendance-button--break"
            :disabled="loading || stampingStatusLoading || !canStartBreak"
            @click="handleTime('breakStart')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-coffee"/></span>
            <span>
              <strong>{{ breakStartButtonLabel }}</strong>
              <small><span v-for="(phrase, i) in breakStartPhrases" :key="i" class="attendance-button__phrase">{{ phrase }}</span></small>
            </span>
          </button>

          <button
            type="button"
            class="attendance-button attendance-button--return"
            :disabled="loading || stampingStatusLoading || !canEndBreak"
            @click="handleTime('breakEnd')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-loop"/></span>
            <span>
              <strong>{{ breakEndButtonLabel }}</strong>
              <small><span v-for="(phrase, i) in breakEndPhrases" :key="i" class="attendance-button__phrase">{{ phrase }}</span></small>
            </span>
          </button>
        </div>
        <p v-if="stampLocationEnabled" class="attendance-location-note" data-testid="attendance-location-note" style="margin: 8px 0 0; font-size: 12px; color: #5c6f80;">
          打刻時に端末の位置情報を記録します。
          <span v-if="stampRestrictEnabled" data-testid="attendance-restrict-note">登録されたエリア外では打刻できません。</span>
        </p>

        <!--
          **打刻は記録できたが、その月の勤務表の計算を保存できなかったとき。**

          打刻の成功の知らせ（「打刻しました」）とは別に、ここへ出す。
          打刻そのものは済んでいるので、押し直させない。保存できたら消える。
          どの月の勤務表かを年月で示し、その下に、勤務表の画面が出すのと
          同じ理由の文を並べる（理由が無い止まり方もある）。

          太字の1行は2つの版がある（rosterSyncNoteHeadline）。この画面で押した退勤の月が
          残っているあいだは退勤の版、開いたときに流した月だけが残っているときは開いたときの版。
        -->
        <div
          v-if="rosterSyncFailures.length"
          class="attendance-roster-sync-note"
          role="status"
          aria-live="polite"
        >
          <strong>{{ rosterSyncNoteHeadline }}</strong>
          <span>勤務時間の計算結果がまだ記録されていないため、勤務表を開いて内容を確認し［保存］してください。</span>
          <div
            v-for="failure in rosterSyncFailures"
            :key="failure.key"
            class="attendance-roster-sync-note__month"
          >
            <span class="attendance-roster-sync-note__label">{{ failure.label }}</span>
            <ul v-if="failure.reasons.length">
              <li v-for="(reason, reasonIndex) in failure.reasons" :key="reasonIndex">{{ reason }}</li>
            </ul>
          </div>
        </div>

        <div
          v-if="stampingStatusLoaded && useProjectBreakFlg"
          class="attendance-break-note"
        >
          <strong>固定休憩は出勤時に反映済みです。</strong>
          <span>追加で発生した休憩だけを開始・終了してください。</span>
        </div>

        <!--
          定時を過ぎた分を休憩に入れる会社だけに出す。
          使っていない会社に出すと、起きないことを注意することになる。
        -->
        <div
          v-if="stampingStatusLoaded && postWorkBreakFlg"
          class="attendance-break-note attendance-break-note--post-work"
        >
          <strong>定時を過ぎて退勤すると、自動で休憩が加算される場合があります。</strong>
          <span>実際には作業・待機・電話対応等を行っていた場合は、勤務表で休憩時間を修正してください。</span>
        </div>

        <section
          v-if="stampingStatusLoaded"
          class="attendance-stamp-result"
          aria-labelledby="attendance-stamp-result-title"
          aria-live="polite"
        >
          <div class="attendance-stamp-result__header">
            <div>
              <p>STAMP RESULT</p>
              <h3 id="attendance-stamp-result-title">打刻結果</h3>
            </div>
            <span v-if="businessDate" class="attendance-stamp-result__date">
              勤務日 {{ formatBusinessDate(businessDate) }}
            </span>
          </div>
          <div v-if="attendanceStatus === 'SCHEDULED'" class="attendance-scheduled-note">
            勤務表で事前保存された定時候補です。出勤ボタンで現在時刻の実績へ切り替えます。
          </div>
          <div class="attendance-stamp-result__grid">
            <div class="attendance-stamp-result__item attendance-stamp-result__item--start">
              <span class="attendance-stamp-result__label">出勤</span>
              <strong>{{ startTime || '--:--' }}</strong>
              <small>勤務開始</small>
            </div>

            <div class="attendance-stamp-result__item attendance-stamp-result__item--end">
              <span class="attendance-stamp-result__label">退勤</span>
              <strong>{{ endTimeDisplay }}</strong>
              <small>勤務終了</small>
            </div>

            <div class="attendance-stamp-result__item attendance-stamp-result__item--break">
              <span class="attendance-stamp-result__label">休憩合計</span>
              <strong>{{ breakTime || '00:00' }}</strong>
              <!-- 「＋」の前でだけ折り返す（字を 12px にしたら、狭い枠で言葉の途中で折れた）。字は breakSummaryDescription のまま。 -->
              <small><span v-for="(phrase, i) in breakSummaryPhrases" :key="i" class="attendance-stamp-result__phrase">{{ phrase }}</span></small>
            </div>

            <div class="attendance-stamp-result__item attendance-stamp-result__item--night">
              <span class="attendance-stamp-result__label">夜勤休憩</span>
              <strong>{{ nightRest || '00:00' }}</strong>
              <small>22:00〜05:00</small>
            </div>
          </div>
        </section>

        <div
          v-else-if="stampingStatusError"
          class="attendance-status-error"
          role="alert"
        >
          <div>
            <strong>打刻情報を取得できませんでした</strong>
            <small>安全のため打刻ボタンを無効化しています。</small>
          </div>
          <button
            type="button"
            :disabled="stampingStatusLoading"
            @click="fetchStampingStatus"
          >
            再取得
          </button>
        </div>

        <div v-if="loading" class="attendance-loading">
          <span class="attendance-loading__spinner"></span>
          打刻情報を送信しています
        </div>
      </article>

      <!--
        **開ける画面が1つも無いときは、カードごと出さない。**見出しだけの
        カードは、何をすればいいかが読めない（2026-10-08 の決め）。
        権限の文脈を読めなかったときも同じ。
      -->
      <article v-if="quickMenu.length" class="dashboard-card quick-card">
        <div class="card-heading">
          <div>
            <p class="card-heading__eyebrow">QUICK ACCESS</p>
            <h2>よく使うメニュー</h2>
          </div>
          <CIcon name="cil-apps" class="card-heading__icon"/>
        </div>

        <div class="quick-menu">
          <router-link
            v-for="item in quickMenu"
            :key="item.label"
            :to="item.to"
            class="quick-menu__item"
          >
            <span class="quick-menu__icon" :class="'quick-menu__icon--' + item.tone">
              <CIcon :name="item.icon"/>
            </span>
            <span class="quick-menu__text">
              <strong>{{ item.label }}</strong>
              <small>{{ item.description }}</small>
            </span>
            <CIcon name="cil-chevron-right" class="quick-menu__arrow"/>
          </router-link>
        </div>
      </article>
    </section>

    <!--
      **未申請は、どの月の数字かを数字の横に出す。**締め中の月（締めの日までは
      前月）と、前月の出し忘れを数える。暦の今月だけで数えていたので、10/3 に
      9月の未申請があっても「未申請 0」だった（束18）。

      **差戻しの横には月を出さない。**差戻しは月を問わず、出し直していないものを
      全部数える（2026-10-05 のテスターの報告の直し）。未申請を数えた月を横に
      出すと、8月の差戻しを数えているのに「（9・10月）」と読める。
    -->
    <section class="metric-grid">
      <router-link to="/submission_approval/report_list" class="metric-card metric-card--blue">
        <span class="metric-card__icon"><CIcon name="cil-inbox"/></span>
        <span class="metric-card__body"><small>未申請{{ countMonthsSuffix }}</small><strong>{{ actionCounts.unsubmittedCount || 0 }}</strong><span>月次書類の未申請</span></span>
      </router-link>
      <router-link to="/submission_approval/report_list" class="metric-card metric-card--orange">
        <span class="metric-card__icon"><CIcon name="cil-warning"/></span>
        <span class="metric-card__body"><small>差戻し</small><strong>{{ actionCounts.rejectedCount || 0 }}</strong><span>再対応が必要</span></span>
      </router-link>
      <router-link to="/submission_approval/submission_management" class="metric-card metric-card--purple">
        <span class="metric-card__icon"><CIcon name="cil-check-circle"/></span>
        <span class="metric-card__body"><small>承認待ち</small><strong>{{ actionCounts.approvalWaitingCount || 0 }}</strong><span>確認が必要な申請</span></span>
      </router-link>
      <div class="metric-card metric-card--green">
        <span class="metric-card__icon"><CIcon name="cil-clock"/></span>
        <span class="metric-card__body"><small>今月の勤務時間</small><strong class="metric-card__time">{{ formatDuration(monthlySummary.workTime) }}</strong><span>{{ monthlySummary.attendanceDays || 0 }}日 出勤</span></span>
      </div>
    </section>

    <!--
      前月ぶんの請求書。**請求書管理を開ける人にだけ、サーバが返す。**
      経営管理者が毎月いちばん忘れてはいけない仕事が、毎日開く画面に
      出ていなかった（束34）。押すと、その月を渡して集計（開けなければ
      請求書管理）を開く。
    -->
    <div v-if="billingLine" class="preika-billing-line" role="status">
      <CIcon name="cil-description"/>
      <span class="preika-billing-line__text">{{ billingLine.text }}</span>
      <button
        v-if="billingLine.route"
        type="button"
        class="preika-billing-line__link"
        @click="openBillingTarget"
      >{{ billingLine.linkLabel }}<CIcon name="cil-arrow-right"/></button>
    </div>

    <section class="preika-insight-layout preika-insight-layout--personal">
      <DashboardActionCenter
        :items="actionItems"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
      />
      <DashboardWorkProjection
        :projection="workProjection"
        :management-view="managementView"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
      />
    </section>

    <!--
      **承認滞留分析は、締めのカードとは別の条件で出す**（2026-10-08、監査）。
      締めのカードの条件（managementView）の内側に置いていたので、自分宛ての承認だけを
      扱う人には、上段の「承認待ち」が1件あっても、承認滞留分析がカードごと出なかった。
      どちらを出すかは、サーバが返す印で決める。
        段 … 締めのカードを出す人（managementView）か、承認滞留分析だけを出す人で
             承認待ちが1件以上ある人（approvalAgingAloneShown）
        締めコックピット … managementView の人だけ（広げない）
        承認滞留分析 … 段が出る人の全員（締めのカードを出す人には、今までどおり0件でも出す）
      カードが1枚のときは、2列の並びにしない（右側が空いたまま並ぶ）。
      **承認滞留分析だけの人には、承認待ちが1件以上のときだけ出す**（approvalAgingAloneShown）。
    -->
    <section
      v-if="managementView || approvalAgingAloneShown"
      class="preika-insight-layout"
      :class="managementView ? 'preika-insight-layout--management' : 'preika-insight-layout--single'"
    >
      <DashboardCloseCockpit
        v-if="managementView"
        :summary="closeCockpit"
        :access-context="accessContext"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
        @page="fetchDashboardInsights"
        @condition="applyCloseCondition"
      />
      <DashboardApprovalAging
        :aging="approvalAging"
        :access-context="accessContext"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
      />
    </section>

    <!--
      今日の勤怠状況。**経営管理者以上だけ。**
      権限が無ければ問い合わせもしない。呼んで403にすると、利用者からは
      壊れて見える（ダッシュボードの打刻カードで同じことが起きている）。
    -->
    <section v-if="attendanceOverviewView" class="preika-insight-layout">
      <DashboardAttendanceOverview
        :overview="attendanceOverview"
        :keyword="attendanceOverviewKeyword"
        :status-filter="attendanceOverviewStatus"
        :loading="attendanceOverviewLoading"
        :error="attendanceOverviewError"
        @retry="fetchAttendanceOverview"
        @page="fetchAttendanceOverview"
        @filter="applyAttendanceOverviewFilter"
      />
    </section>

    <!--
      36協定の上限に触れている方。**経営管理者以上だけ。**

      フラグは attendanceOverviewView と同じ1回の /access-context から作る。
      **managementView を使わない。**あちらは事務担当者（月次集計の参照）でも
      true になるので、ここより広い（2026-09-30 に旧区分での上書きはやめた）。

      **該当者が0名なら描かない。**出ていること自体が信号になる。
      判定できなかった方も「触れている」に含めて数えるので、
      見ていないことが黙って消えることはない。
    -->
    <!--
      **届いていないことは、カードが出ない月にも出す。**

      カードは該当者が0名なら描かない（出ていること自体が信号）。だが
      通知先を1つも決めていない会社は、まさにその月に何も出ないまま
      翌月へ行く。次の月に超過者が出ても、お知らせは1通も届かない——
      その会社が気づく機会は、ここに帯を出す以外に無い。

      値は同じ1回の応答に既に入っている。出すだけで塞がる。
    -->
    <div
      v-if="overtimeLimitView && overtimeLimitDeliveryNotice"
      class="preika-delivery-notice"
      :class="'preika-delivery-notice--' + overtimeLimitDeliveryNotice.tone"
      role="status"
    >
      <CIcon name="cil-bell"/>
      <div>
        <strong>{{ overtimeLimitDeliveryNotice.headline }}</strong>
        <small>{{ overtimeLimitDeliveryNotice.detail }}</small>
      </div>
    </div>

    <section
      v-if="overtimeLimitView && overtimeLimitTotal > 0"
      class="preika-insight-layout"
    >
      <DashboardOvertimeLimit :status="overtimeLimitStatus"/>
    </section>

    <section class="dashboard-grid dashboard-grid--overview" :class="{ 'dashboard-grid--single': !monthlyDocuments.length }">
      <article class="dashboard-card monthly-summary-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">MONTHLY PERFORMANCE</p><h2>今月の勤務サマリー</h2></div>
          <!--
            **見出しの月は、中身を引いたサーバの月。**ブラウザの時計で出すと、
            サーバが前月を引いた朝（UTC の本番の1日の朝）に見出しだけ今月になる（束41）。
          -->
          <span class="summary-month">{{ summaryMonthLabel }}</span>
        </div>
        <div class="summary-list">
          <div v-for="item in summaryItems" :key="item.label" class="summary-item">
            <span class="summary-item__label">{{ item.label }}</span><strong>{{ item.value }}</strong>
          </div>
        </div>
        <!--
          **時間外の数字のすぐ下に置く。**別のカードへ離すと、
          「時間外 36時間00分」を見た人が、限度に対してどこなのかを
          別の場所へ探しにいくことになる。

          限度が分からない会社（36協定が未登録・法令判定が切ってある）
          では、部品の側が自分で出ないと決める。
        -->
        <DashboardMyOvertimeGauge
          class="monthly-summary-card__gauge"
          :overtime-minutes="myOvertimeMinutes"
          :limit-minutes="myOvertimeLimitMinutes"
          :threshold-percent="myOvertimeThresholdPercent"
          :incomplete="myOvertimeIncomplete"
        />
      </article>

      <!--
        **出す書類が1つも無いときは、カードごと出さない**（よく使うメニューと同じ決め）。
        見出しと［一覧を見る］だけのカードになり、その［一覧を見る］も開けない。
      -->
      <article v-if="monthlyDocuments.length" class="dashboard-card monthly-status-card">
        <div class="card-heading">
          <div>
            <p class="card-heading__eyebrow">MONTHLY STATUS</p><h2>月次提出状況</h2>
            <small v-if="closingMonthLabel" class="monthly-status-card__month">{{ closingMonthLabel }}</small>
          </div>
          <router-link to="/submission_approval/report_list" class="text-link">一覧を見る<CIcon name="cil-arrow-right"/></router-link>
        </div>
        <!--
          **行はそれぞれの画面へのリンク。**押せないと、状態を見たあとに
          左メニューから探し直すことになる（束34）。
          **会社で使っていない書類は出さない。**左メニューと同じ判定
          （canAccessPath）で絞る。経費を切った会社に「経費 未登録」が
          ずっと並んでいた（束18）。
        -->
        <div class="document-status-list">
          <router-link
            v-for="document in monthlyDocuments"
            :key="document.documentType"
            :to="document.to"
            class="document-status"
          >
            <span class="document-status__icon"><CIcon :name="document.icon"/></span>
            <span class="document-status__name">{{ document.documentName }}</span>
            <span class="document-status__badge" :class="'document-status__badge--' + document.tone">{{ document.statusName }}</span>
          </router-link>
        </div>
      </article>
    </section>

    <!--
      お知らせが1件も無ければ、カードごと出さない。**空のカードは
      「何かあるはずなのに無い」に見える。**そのぶん提出ガイドを広げる。
    -->
    <section class="dashboard-grid dashboard-grid--middle"
             :class="{ 'dashboard-grid--single': notices.length === 0 }">
      <article v-if="notices.length" class="dashboard-card notice-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">INFORMATION</p><h2>お知らせ</h2></div>
          <span class="notice-count">{{ notices.length }} NEWS</span>
        </div>
        <div class="notice-list">
          <div v-for="notice in notices" :key="notice.id" class="notice-item">
            <div class="notice-item__date">{{ noticeDate(notice) }}</div>
            <div class="notice-item__content">
              <span class="notice-item__category" :class="'notice-item__category--' + noticeTone(notice)">{{ noticeCategoryLabel(notice.category) }}</span>
              <p>{{ notice.body }}</p>
            </div>
          </div>
        </div>
      </article>

      <article class="dashboard-card submission-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">SUBMISSION GUIDE</p><h2>提出ガイド</h2></div>
          <CIcon name="cil-paper-plane" class="card-heading__icon"/>
        </div>
        <div class="submission-guide">
          <!--
            月次の締めは会社ごと。**決めていない会社では、この枠ごと
            出さない。**2026-08-27 まで「第2営業日まで」と直に書かれて
            いて、これは販売元の締めだった。他社の社員は毎日、自社と
            違う期限を見せられていた。
          -->
          <div v-if="submissionDue" class="submission-guide__item submission-guide__item--monthly">
            <span class="submission-guide__number">{{ submissionDue.number }}</span>
            <div>
              <strong>{{ submissionDue.label }}</strong>
              <p v-if="submissionDue.items">{{ submissionDue.items }}</p>
            </div>
          </div>
          <div class="submission-guide__item submission-guide__item--anytime">
            <span class="submission-guide__number"><CIcon name="cil-bell"/></span>
            <div><strong>わかり次第、早めに</strong><p>休暇・予定外時間外・休日出勤</p></div>
          </div>
        </div>
        <router-link to="/submission_approval/report_list" class="submission-link">自分の申請一覧を確認<CIcon name="cil-arrow-right"/></router-link>
      </article>
    </section>

    <!--
      社内カレンダー。**入れていない会社では、節ごと出さない。**

      2026-08-27 まで、ここには販売元（AKI）の Google カレンダーが直に
      書かれていて、v-if も無かった。買った会社の社員が自社の画面を
      開くと、こちらの社内予定が見えていた。

      **base64 で URL に埋まっていたので、画面のドメイン検査は素通りした。**
    -->
    <section v-if="calendarEmbedUrl" class="dashboard-card calendar-card">
      <div class="card-heading calendar-card__heading">
        <div><p class="card-heading__eyebrow">SCHEDULE</p><h2>社内カレンダー</h2></div>
      </div>
      <div class="calendar-frame">
        <iframe
          :src="calendarEmbedUrl"
          title="社内カレンダー"
          frameborder="0"
          scrolling="no"
        ></iframe>
      </div>
    </section>
  </div>
</template>

<script>
import { currentStampingLocation } from '@/utils/stampingLocation'
import axios from 'axios'
import { PRODUCT_NAME } from '../common/productName'
import moment from 'moment'
import ConstList from './../ConstList'
import { normalizeTimeValue } from '../utils/timeValue'
import {
  formatDurationText,
  formatInsightYearMonth,
  monthListLabel,
  monthOnlyLabel,
  openListWith
} from '../utils/dashboardInsights'
import { canAccessPath } from '../utils/accessPolicy'
import { noticeCategoryLabel } from '../utils/platformNoticeCategory'
import {
  defaultOvertimeLimitMonth,
  overtimeLimitConditionParams,
  overtimeAlertDeliveryNotice
} from '../utils/overtimeLimitStatus'
import { backendErrorMessage } from '../utils/rosterActualPolicy'
import SignInMethodSetupDialog from '@/components/account/SignInMethodSetupDialog'
import DashboardActionCenter from '../common/dashboard/DashboardActionCenter'
import DashboardWorkProjection from '../common/dashboard/DashboardWorkProjection'
import DashboardCloseCockpit from '../common/dashboard/DashboardCloseCockpit'
import DashboardApprovalAging from '../common/dashboard/DashboardApprovalAging'
import DashboardAttendanceOverview from '../common/dashboard/DashboardAttendanceOverview'
import DashboardOvertimeLimit from '../common/dashboard/DashboardOvertimeLimit'
import DashboardMyOvertimeGauge from '../common/dashboard/DashboardMyOvertimeGauge'
import { safeErrorDigest } from '../utils/safeResponseMessage'
import { yearMonthQuery } from '../utils/askedYearMonth'

/*
 * 打刻のあと、その月の勤務表を画面に出さずに流すときの時間切れ。
 *
 * 流す本体も同じ長さで自分を打ち切る。こちらは、その手前（部品の読み込み）で
 * 固まったときのための外枠なので、少しだけ長くしてある。
 * **どちらが効いても「送信中」は必ず終わる。**
 *
 * 長さ：手元の実機で、退勤から「打刻しました」まで 3秒ほど（打刻・勤務表を流す・取り直しを含む）。
 * 通信が詰まったときだけ効く長さでよいので、本体 15秒・外枠の猶予 5秒。
 * 流れなかった月は、次にダッシュボードを開いたときに拾う。
 */
const ROSTER_SYNC_TIMEOUT_MS = 15000
const ROSTER_SYNC_OUTER_GRACE_MS = 5000

/*
 * ほかで先に保存されて断られたあと、計算が済んでいない月を取り直す通信の時間切れ。
 *
 * この取り直しは、流す本体の時間切れの外にある。回線が詰まった・圏外へ出たなどで返事が来ないと、
 * 「送信中」が終わらず、打刻のボタンが押せないままになる。切れたら、流し直さずに知らせを出す。
 */
const ROSTER_SYNC_RECHECK_TIMEOUT_MS = 5000

/*
 * 保存できなくても、打刻のカードへ「勤務表を開いて［保存］してください」と出さない止まり方。
 *
 * - hidden：今日より後の日に打刻の時刻が入っている月。案内どおり勤務表で［保存］すると、
 *   その日の時刻が空で上書きされる。その日が過ぎれば、次に開いたときに記録される。
 * - locked：承認待ち・承認済みの月。勤務表を開いても［保存］は押せない。
 * - unsupported・setup：流す前に止まった（利用者が勤務表で直せる理由ではない）。
 *
 * ここに無い止まり方は、全部出す。**知らない止まり方は、黙らずに出す側へ倒す。**
 */
const ROSTER_SYNC_SILENT_STAGES = Object.freeze(['hidden', 'locked', 'unsupported', 'setup'])

/*
 * ほかで先に保存されて断られた止まり方（保存の通信を送って、版の食い違いで断られた）。
 * 別のタブの勤務表や、もう1つ開いているダッシュボードが、同じ月を先に保存した直後に起きる。
 * 利用者が勤務表で直す理由ではないので、知らせを出す前に、もう1回だけ流し直す
 * （runRosterSyncRetryingConflict）。
 */
const isRosterSyncConflict = result => !!result && result.stage === 'save' && result.status === 409

// 打刻で送る時刻の形（年/月/日 時:分。端末の時刻）。
const formatStampingTimestamp = date => moment(date).format('YYYY/MM/DD HH:mm')

// 「2026-09」「2026-09-30」から、流す月を作る。読めなければ null。
const rosterMonthOf = value => {
  const match = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(String(value || '').trim())
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  if (month < 1 || month > 12) return null
  return { key: `${match[1]}-${match[2]}`, year, month, label: `${year}年${month}月` }
}

/*
 * 理由の文に入っていたら、その文をカードへ並べない文字（半角の英字）。
 *
 * 理由の文は、勤務表の画面とサーバが出す文をそのまま並べる。利用者向けの文は日本語で
 * 書いてあるが、サーバの文の中に、項目の名前が英字のまま入っているものがある
 * （月の合計の値が読めないときの文）。打刻のカードは開いただけで目に入る場所なので、
 * 内部の言葉が入った文は並べない。**文を作り替えない（その文は丸ごと並べない）。**
 * その月は、決まった2段の文と年月だけになる。
 *
 * 英字が1文字でも入っていれば並べない。並べなくても案内（勤務表を開いて［保存］）は出るので、
 * 落としすぎるほうへ倒してある。
 */
const ROSTER_SYNC_INTERNAL_WORD = /[A-Za-z]/

// 理由の文。同じ文を2度並べない。空は捨てる。内部の言葉が入った文は並べない。
const rosterSyncReasons = result => {
  const list = result && Array.isArray(result.reasons) ? result.reasons : []
  const texts = list
    .map(value => String(value == null ? '' : value).trim())
    .filter(Boolean)
    .filter(value => !ROSTER_SYNC_INTERNAL_WORD.test(value))
  return texts.filter((value, index) => texts.indexOf(value) === index)
}

/*
 * 打刻のボタンの説明（「休憩開始後に利用可能」など）を、言葉のまとまりの塊に分ける。
 * 「に・を・は」のあとに漢字が続く所で区切る（つなげると元の字と同じ）。
 * 字を 12px にしてボタンの中が狭くなり、「利用可／能」のように言葉の途中で折れたので、
 * 塊は折らずに、塊と塊のあいだでだけ折る（休憩合計の説明と同じやり方）。
 */
const KANJI = /[一-鿿]/
const descriptionPhrases = text => {
  const phrases = []
  let current = ''
  const chars = Array.from(String(text || ''))
  chars.forEach((char, i) => {
    current += char
    if ('にをは'.includes(char) && KANJI.test(chars[i + 1] || '')) {
      phrases.push(current)
      current = ''
    }
  })
  if (current) phrases.push(current)
  return phrases
}

export default {
  name: 'Dashboard',

  components: {
    SignInMethodSetupDialog,
    DashboardActionCenter,
    DashboardWorkProjection,
    DashboardCloseCockpit,
    DashboardApprovalAging,
    DashboardAttendanceOverview,
    DashboardOvertimeLimit,
    DashboardMyOvertimeGauge
  },

  data () {
    return {
      now: new Date(), timerId: null, loading: false, submitFlg: false,
      useProjectBreakFlg: false, userName: '',
      attendanceStatus: 'NOT_STARTED', businessDate: '',
      startTime: '', endTime: '',
      breakStartedAt: '', breakTime: '00:00', nightRest: '00:00',
      stampingStatusLoaded: false, stampingStatusLoading: false,
      // 会社が「打刻時の位置情報記録」を入れているか。読めなければ false（位置を取らない）。
      stampLocationEnabled: false,
      // 登録した場所の外では打刻させない設定が入っているか。
      stampRestrictEnabled: false,
      stampingStatusError: false,
      /*
       * 会社が「定時後の猶予時間を休憩として扱う」を有効にしているか。
       * **読めなければ false のまま**（休憩の説明も注意書きも、今までと同じ表示）。
       */
      postWorkBreakFlg: false,
      /*
       * 打刻は記録できたが、勤務表の計算を保存できなかった月。
       * `{ key: '2026-09', label: '2026年9月', reasons: [...], afterClockOut }` を月の順に持つ。
       * 空なら何も出さない。保存できた月は、ここから外す。
       * afterClockOut は、この画面で押した退勤のあとに流して保存できなかった月なら true
       * （開いたときに黙って流した月は false）。太字の1行の出し分けに使う。
       */
      rosterSyncFailures: [],
      insightLoading: false, insightError: false,
      /*
       * **応答は、最後に投げた要求のものだけを反映する。**
       *
       * 検索語を変えてすぐ絞り込みを変えると、先に投げた要求の応答が
       * 後から届くことがある。画面の条件は新しいのに一覧が古い、という
       * ずれ方をする。連番を持ち、番号が違う応答は捨てる。
       */
      insightRequestId: 0, attendanceOverviewRequestId: 0,
      authorityName: '', managementView: false, actionItems: [],
      // 承認滞留分析を出すか。締めのカード（managementView）とは別に、サーバが決める。
      approvalAgingView: false,
      workProjection: {}, closeCockpit: {}, approvalAging: {},
      // 今日の勤怠状況。経営管理者以上だけ。managementView より狭い。
      attendanceOverviewView: false, attendanceOverview: {},
      /*
       * 36協定の上限。今日の勤怠状況と同じ狭さで、同じ1回の
       * /access-context から決める。**dashboard/insights へ相乗りさせない。**
       * あちらは締めの「次へ」で何度も取り直すので、相乗りさせると
       * 全社ぶんの上限判定がそのたびに走る。
       */
      overtimeLimitView: false, overtimeLimitStatus: {},
      // 自分の1か月の限度時間と、会社が決めた予告の割合。
      // **協定が無ければ空のまま**——45時間と決め打ちで描かない。
      myOvertimeLimit: {},
      attendanceOverviewLoading: false, attendanceOverviewError: false,
      // 絞り込みの条件は親が持つ。サーバへ渡して全員から絞るため。
      attendanceOverviewKeyword: '', attendanceOverviewStatus: '',
      // 月次締めも同じ。既定は未完了（コックピットのチップの初期値と揃える）。
      closeQuery: '', closeState: 'INCOMPLETE',
      actionCounts: {}, monthlySummary: {}, submissions: [],
      /*
       * どの月の数字か（サーバが決める）。締め中の月・件数を数えた月・
       * 今月の勤務時間の月。**ブラウザの時計では決めない。**
       */
      closingYearMonth: '', countYearMonths: [], summaryYearMonth: '',
      /* 前月ぶんの請求書。請求書管理を開ける人にだけサーバが返す。 */
      billingStatus: null,
      /*
       * 本人の役割・会社の機能・権限（/access-context）。**左メニューと
       * 同じ判定（canAccessPath）で、よく使うメニューと月次提出状況を絞る。**
       * 読めないうちは null で、開けるか分からない入口を出さない。
       */
      accessContext: null,
      /*
       * よく使うメニューの候補。**このまま出さない。**会社の機能の設定と
       * 本人の権限で絞った quickMenu を出す（経費を切った会社に「経費精算」が
       * 出て、押すと開けなかった。束18）。
       */
      quickMenuCandidates: [
        { label: '勤務表', description: '日々の勤務実績を入力', to: '/report/roster', icon: 'cil-calendar-check', tone: 'blue' },
        { label: '業務報告', description: '月次の業務内容を報告', to: '/report/monthlyreport', icon: 'cil-description', tone: 'navy' },
        { label: '経費精算', description: '業務経費を登録・申請', to: '/payoff/expense', icon: 'cil-wallet', tone: 'green' },
        { label: '通勤費精算', description: '通勤経路・交通費を登録', to: '/payoff/travelCost', icon: 'cil-bus-alt', tone: 'cyan' },
        { label: '休暇申請', description: '有休・代休・特休・欠勤を申請', to: '/paidandsubstitutes/list', icon: 'cil-sun', tone: 'orange' },
        { label: '自分の申請一覧', description: '提出状況を確認', to: '/submission_approval/report_list', icon: 'cil-send', tone: 'purple' },
        { label: '承認待ちの申請', description: '確認が必要な申請', to: '/submission_approval/submission_management', icon: 'cil-check-circle', tone: 'cyan' }
      ],
      /** 会社ごとの社内カレンダー。空なら節ごと出さない。 */
      calendarEmbedUrl: '',
      /* 月次提出の締め。**空は「決めていない」。** */
      submissionDueKind: '',
      submissionDueDay: null,
      submissionDueItems: '',
      /*
       * お知らせ配信。**サーバから来たものだけを出す。**
       *
       * 2026-08-27 まで、ここには3件が直に書かれていた。うち2件は
       * 2025.09.10 の「〜になりました」で、1年近く前の知らせを
       * 新しく契約した会社の社員が毎日見ていた。既定を持たせない。
       */
      notices: []
    }
  },
  computed: {
    /**
     * 承認滞留分析だけを出す人（締めのカードは出ない人）に、カードを出すか。
     *
     * **承認待ちが1件以上あるときだけ出す。**「担当申請の承認」は、標準の権限グループでは
     * 一般社員・事務担当者・社内システム管理者も持っている。件数を見ないと、申請・承認を
     * 使う会社のほぼ全員に「承認待ちはありません」のカードが出る（この画面の決め
     * 「0件のカードは出さない」と同じ扱い。上段の「承認待ち」の数字は今までどおり出る）。
     * 締めのカードを出す人には、今までどおり0件でも出す（段の条件が managementView を見る）。
     */
    approvalAgingAloneShown () {
      if (this.managementView || !this.approvalAgingView) return false
      return Number((this.approvalAging || {}).totalCount || 0) > 0
    },

    /**
     * 今月の法定時間外労働（分）。
     *
     * **サマリーの「時間外」は使わない。**あれは画面が日次を合計して
     * 保存した値（t_monthly.sum_over_time）で、休日労働と同じ時間が
     * 両方に入るうえ、**週40時間（労基法32条1項）の超過が入っていない。**
     * 日8時間ずつ週6日の人は「時間外0時間」になる。
     *
     * 36協定の限度時間と並べる数字なので、判定と同じ定義でなければ
     * 意味が無い。サーバが判定と同じ口から取った値をそのまま使う。
     */
    myOvertimeMinutes () {
      const value = Number(this.myOvertimeLimit.overtimeMinutes)
      return Number.isFinite(value) && value >= 0 ? value : null
    },

    /**
     * 読み取れていない日があるか。
     *
     * <b>合計は小さいほうへずれる。</b>「まだ余裕がある」と見えるほうへ
     * 倒れるので、そのときは数字だけを黙って出さない。
     */
    myOvertimeIncomplete () {
      return this.myOvertimeLimit.incomplete === true
    },

    /**
     * 1か月の限度時間（分）。
     *
     * **45時間と決め打ちしない。**1年単位の変形労働時間制なら42時間で、
     * 協定で別の値を決めている会社もある。協定が無ければ null で、
     * カードそのものが出ない。
     */
    myOvertimeLimitMinutes () {
      const value = Number(this.myOvertimeLimit.limitMinutes)
      return value > 0 ? value : null
    },

    /** 会社が決めた予告の割合。決めていなければ null で、線を引かない。 */
    myOvertimeThresholdPercent () {
      const value = Number(this.myOvertimeLimit.thresholdPercent)
      return value > 0 ? value : null
    },

    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    },

    /**
     * 36協定の上限に触れている方の合計。
     *
     * **サーバが数えた合計をそのまま使う。**画面で数え直すと、
     * カードに並べた3名だけを見て「3名」と出す形になる。
     * 判定できなかった方もこの合計に入っているので、
     * 見ていないことがあればカードは出る。
     */
    overtimeLimitTotal () {
      return Number(this.overtimeLimitStatus.totalCount) || 0
    },

    /**
     * 上限のお知らせが届いていないこと。**カードとは別に出す。**
     *
     * 幹部が毎日開くのはこの画面で、専用画面はお知らせを受けてから開く。
     * つまり届いていない会社では専用画面が開かれない。**届いていないと
     * いちばん伝わらない相手が、いちばん見ている画面で黙っていた。**
     *
     * 応答が返っていないあいだ（blocker が空）は黙る。読み込み中の一瞬に
     * 「届いていません」を出すと、正しく届いている会社にも毎回見える。
     */
    overtimeLimitDeliveryNotice () {
      const status = this.overtimeLimitStatus || {}
      if (!status.notificationBlocker) return null
      return overtimeAlertDeliveryNotice(
        status.notificationBlocker,
        status.notificationUsingSharedChannel === true,
        status.notificationLastSendFailed === true
          ? (status.notificationLastFailedMonth || '')
          : ''
      )
    },

    /**
     * 提出ガイドに出す、月次の締め。
     *
     * **数え方と日が揃っていなければ null。**揃っていない状態を画面で
     * 補うと、どちらか一方だけ設定した会社に中途半端な案内が出る。
     * サーバもDBも対で縛っているので、ここも対で見る。
     */
    submissionDue () {
      const day = Number(this.submissionDueDay)
      if (!this.submissionDueKind || !day) { return null }
      const label = this.submissionDueKind === 'BUSINESS_DAY'
        ? '第' + day + '営業日まで'
        : day + '日まで'
      return {
        number: ('0' + day).slice(-2),
        label,
        items: this.submissionDueItems
      }
    },

    /**
     * 画面のいちばん上の挨拶。
     *
     * **名前を呼び捨てにしない。**「さん」を付ける。名前が読めないときは
     * 名前ごと出さない——「おはようございます、さん」になるくらいなら、
     * 挨拶だけのほうがよい（以前は「メンバー」と出していた）。
     */
    greeting () {
      const hour = this.now.getHours()
      const time = hour < 11 ? 'おはようございます' : 'おつかれさまです'
      return this.userName ? time + '、' + this.userName + 'さん' : time
    },
    formattedDate () { return moment(this.now).format('YYYY年 M月 D日') },
    formattedDateWithWeekday () { const weekdays = ['日', '月', '火', '水', '木', '金', '土']; return moment(this.now).format('YYYY年 M月 D日') + '（' + weekdays[this.now.getDay()] + '）' },
    formattedTime () { return moment(this.now).format('HH:mm:ss') },
    currentYearMonth () { return moment(this.now).format('YYYY年M月') },
    attendanceStatusLabel () {
      if (this.stampingStatusError) return '取得失敗'
      return ({
         NOT_STARTED: '未打刻',
         SCHEDULED: '定時候補あり',
        WORKING: '業務中',
        BREAK: '休憩中',
        FINISHED: '退勤済み'
      })[this.attendanceStatus] || '未打刻'
    },
    endTimeDisplay () {
      if (!this.endTime) return '--:--'
      if (!this.startTime) return this.endTime

      const start = moment(this.startTime, 'HH:mm', true)
      const end = moment(this.endTime, 'HH:mm', true)
      if (!start.isValid() || !end.isValid()) return this.endTime

      return end.isBefore(start)
        ? `${this.endTime}（翌日）`
        : this.endTime
    },
    canStartAttendance () {
      return this.stampingStatusLoaded && ['NOT_STARTED', 'SCHEDULED'].includes(this.attendanceStatus)
    },
    canEndAttendance () {
      return this.stampingStatusLoaded && this.attendanceStatus === 'WORKING'
    },
    canStartBreak () {
      return this.stampingStatusLoaded &&
        this.attendanceStatus === 'WORKING'
    },
    canEndBreak () {
      return this.stampingStatusLoaded && this.attendanceStatus === 'BREAK'
    },
    breakStartLabel () {
      if (!this.breakStartedAt) return ''
      const start = moment(this.breakStartedAt, 'YYYY/MM/DD HH:mm', true)
      if (!start.isValid()) return ''
      return start.format('MM/DD HH:mm')
    },
    breakElapsedLabel () {
      if (!this.breakStartedAt) return ''
      const start = moment(this.breakStartedAt, 'YYYY/MM/DD HH:mm', true)
      if (!start.isValid()) return ''
      const minutes = Math.max(0, moment(this.now).diff(start, 'minutes'))
      const hours = Math.floor(minutes / 60)
      const restMinutes = String(minutes % 60).padStart(2, '0')
      return `${hours}:${restMinutes}`
    },
    breakStartButtonLabel () {
      return this.useProjectBreakFlg ? '追加休憩開始' : '休憩開始'
    },
    breakEndButtonLabel () {
      return this.useProjectBreakFlg ? '追加休憩終了' : '休憩終了'
    },
    breakSummaryDescription () {
      // 定時を過ぎた分を休憩に入れる会社では、その分も合計に入っている。
      if (this.postWorkBreakFlg) {
        return this.useProjectBreakFlg ? '固定休憩＋追加休憩＋定時後休憩' : '打刻した休憩＋定時後休憩の合計'
      }
      return this.useProjectBreakFlg ? '固定休憩＋追加休憩' : '打刻した休憩の累計'
    },
    /* 休憩合計の説明を「＋」の前で区切った塊（つなげると breakSummaryDescription と同じ字）。 */
    breakSummaryPhrases () {
      return this.breakSummaryDescription.split('＋').map((phrase, i) => (i ? '＋' : '') + phrase)
    },
    /*
     * 勤務表の計算を保存できなかったときの知らせの、太字の1行。
     *
     * - この画面で押した退勤の月が、保存できずに残っているあいだ：退勤の版。
     * - 開いたときに黙って流した月だけが残っているとき：開いたときの版。
     *   まだ今日の出勤も押していない朝に「退勤の打刻は完了しています」と出すと、
     *   画面の状態（未出勤）と食い違う。対象も今日ではなく過去の日。
     * - 両方が残っているとき：退勤の版（いま押した退勤が主）。
     */
    rosterSyncNoteHeadline () {
      return this.rosterSyncFailures.some(failure => failure.afterClockOut === true)
        ? '退勤の打刻は完了しています。再度押す必要はありません。'
        : '過去の打刻は完了しています。再度押す必要はありません。'
    },
    breakStartDescription () {
      if (this.attendanceStatus === 'BREAK') return '現在休憩中です'
      if (this.attendanceStatus !== 'WORKING') return '出勤後に利用可能'
      if (this.useProjectBreakFlg) return '固定休憩とは別に記録'
      return '休憩を記録する'
    },
    breakEndDescription () {
      if (this.attendanceStatus !== 'BREAK') return '休憩開始後に利用可能'
      const start = this.breakStartLabel ? `${this.breakStartLabel} 開始` : '休憩中'
      const elapsed = this.breakElapsedLabel ? `（${this.breakElapsedLabel}経過）` : ''
      return start + elapsed
    },
    /* 休憩開始・終了のボタンの説明を、言葉のまとまりの塊にしたもの（つなげると元の説明と同じ字）。 */
    breakStartPhrases () {
      return descriptionPhrases(this.breakStartDescription)
    },
    breakEndPhrases () {
      return descriptionPhrases(this.breakEndDescription)
    },
    summaryItems () {
      return [
        { label: '総勤務時間', value: this.formatDuration(this.monthlySummary.workTime) },
        { label: '時間外', value: this.formatDuration(this.monthlySummary.overTime) },
        { label: '休日勤務', value: this.formatDuration(this.monthlySummary.holidayTime) },
        { label: '深夜勤務', value: this.formatDuration(this.monthlySummary.nightTime) },
        { label: '有休使用', value: (this.monthlySummary.paidHolidayUsed || 0) + '日' },
        { label: '有休残数', value: (this.monthlySummary.paidHolidayRemain || 0) + '日' }
      ]
    },
    /** 左メニューと同じ判定で絞った、よく使うメニュー。 */
    quickMenu () {
      if (!this.accessContext) return []
      return this.quickMenuCandidates.filter(item => canAccessPath(this.accessContext, item.to))
    },

    /** 「（9月）」「（9・10月）」。未申請を数えた月（差戻しは月を問わないので、差戻しの横には出さない）。 */
    countMonthsSuffix () {
      const label = monthListLabel(this.countYearMonths)
      return label ? '（' + label + '）' : ''
    },

    /** 月次提出状況の見出しの月（「2026年9月分」）。 */
    closingMonthLabel () {
      const label = formatInsightYearMonth(this.closingYearMonth)
      return label ? label + '分' : ''
    },

    /** 今月の勤務サマリーの見出し。サーバが返さなければ、これまでどおり画面の今月。 */
    summaryMonthLabel () {
      return formatInsightYearMonth(this.summaryYearMonth) || this.currentYearMonth
    },

    /**
     * 前月ぶんの請求書の1行。
     *
     * 未登録なら「9月分の請求書が未登録です」と、金額を埋めるプロジェクト集計への
     * 道を出す。集計を開けない人は請求書管理へ。
     *
     * **括弧の中（金額未設定の件数）の字は、まだ決まっていない**（2026-10-08。
     * 前半と登録済みの文は決まった文。括弧の中は、開いた先のプロジェクト集計の
     * 札の字「金額未設定」と同じ言葉のまま置いている）。
     */
    billingLine () {
      const status = this.billingStatus
      if (!status || !status.yearMonth) return null
      const month = monthOnlyLabel(status.yearMonth)
      if (!month) return null
      const canSummary = canAccessPath(this.accessContext, '/admin/total/list_project')
      const canSales = canAccessPath(this.accessContext, '/admin/sales/salesManagement')
      if (status.registered === true) {
        return {
          text: month + '分の請求書が登録済みです',
          route: canSales ? '/admin/sales/salesManagement' : '',
          linkLabel: '請求書管理へ'
        }
      }
      const unset = Number(status.unsetRateCount) || 0
      const target = canSummary
        ? { route: '/admin/total/list_project', linkLabel: 'プロジェクト集計へ' }
        : canSales
          ? { route: '/admin/sales/salesManagement', linkLabel: '請求書管理へ' }
          : { route: '', linkLabel: '' }
      return {
        text: month + '分の請求書が未登録です' + (unset > 0 ? '（金額未設定 ' + unset + '件）' : ''),
        route: target.route,
        linkLabel: target.linkLabel
      }
    },

    monthlyDocuments () {
      const definitions = [
        { documentType: '01', documentName: '勤務表', icon: 'cil-calendar-check', to: '/report/roster' },
        { documentType: '02', documentName: '業務報告', icon: 'cil-description', to: '/report/monthlyreport' },
        { documentType: '03', documentName: '経費', icon: 'cil-wallet', to: '/payoff/expense' },
        { documentType: '04', documentName: '通勤費', icon: 'cil-bus-alt', to: '/payoff/travelCost' }
      ].filter(definition => this.accessContext && canAccessPath(this.accessContext, definition.to))
      /*
       * **行の月で開く**（2026-10-08、監査）。行き先の画面は、開いたときの月を自分で決める
       * （9日までは前月、10日からは今月）。会社の締めが9日でないと、「9月分」の行を押した先が
       * 10月で開いていた。締め中の月を付けて渡す（読めないときは今までどおり月なしで開く）。
       */
      const query = yearMonthQuery(this.closingYearMonth)
      return definitions.map(definition => {
        const status = this.submissions.find(item => String(item.documentType) === definition.documentType)
        return {
          ...definition,
          to: query ? { path: definition.to, query } : definition.to,
          statusName: status ? status.statusName : '未登録',
          tone: this.getStatusTone(status ? String(status.status) : '')
        }
      })
    }
  },
  created () {
    /*
     * 勤務表を流すときの覚え。画面へは出さないので data には入れない。
     * - rosterSyncGeneration：打刻が始まるたびに進む番号。それより前に始めた「黙って流す」分を見分ける。
     * - rosterSyncTriedMonths：この表示で、開いたときにもう流した月。
     * - rosterSyncLeft：画面を離れた。流している最中の月を、保存へ進ませない。
     */
    this.rosterSyncGeneration = 0
    this.rosterSyncTriedMonths = {}
    this.rosterSyncLeft = false
    // 月次締めの一覧の、最後に取りに行ったページの位置（fetchDashboardInsights が覚える）。
    this.insightCloseOffset = 0
  },
  mounted () {
    this.timerId = window.setInterval(() => { this.now = new Date() }, 1000)
    this.fetchDashboard()
    this.fetchDashboardInsights()
    this.resolveAttendanceOverviewView()
    this.fetchProjectBreakSetting()
    this.fetchPostWorkBreakSetting()
    /*
     * 打刻したのに勤務表の計算が済んでいない月を、黙って流す。
     * **打刻の状態が取れてから。**状態が「定時候補あり」のときは流してはいけないので、
     * 状態が分かる前には始めない。
     */
    this.fetchStampingStatus().then(() => this.syncPendingRosterMonths())
    this.fetchStampLocationSetting()
  },
  beforeDestroy () {
    if (this.timerId) window.clearInterval(this.timerId)
    // 画面を離れたら、まだ始めていない月は流さない（離れた先の勤務表と、裏の保存がぶつからないように）。
    // 流している最中の月も、まだ保存を送っていなければ、送らせない。流れなかった月は、次に開いたときに拾う。
    this.rosterSyncGeneration += 1
    this.rosterSyncLeft = true
  },
  methods: {
    /*
     * 「時:分」を組み立て直して出す。**先頭5文字で切らない。**
     * 100時間を超えると「171:30」が「171:3」になっていた（束35）。
     */
    formatDuration (value) { return formatDurationText(value) },
    noticeCategoryLabel (category) { return noticeCategoryLabel(category) },
    /** 請求書の1行の先を、その月を渡して開く。 */
    openBillingTarget () {
      const line = this.billingLine
      if (!line || !line.route) return
      openListWith(this.$router, line.route, this.billingStatus.yearMonth)
    },
    getStatusTone (status) { return ({ '1': 'neutral', '2': 'pending', '3': 'rejected', '4': 'approved' })[status] || 'empty' },
    fetchDashboard () {
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/dashboard').then(response => {
        const data = response.data || {}
        this.userName = data.userName || ''
        this.actionCounts = data.actionCounts || {}
        this.monthlySummary = data.monthlySummary || {}
        this.closingYearMonth = data.closingYearMonth || ''
        this.countYearMonths = Array.isArray(data.countYearMonths) ? data.countYearMonths : []
        this.summaryYearMonth = data.summaryYearMonth || ''
        // **協定が無ければ空のまま。**部品の側が自分で出ないと決める。
        this.myOvertimeLimit = data.myOvertimeLimit || {}
        this.submissions = data.submissions || []
        /*
         * **読めなければ出さない。**空文字にしておくと v-if が偽になり、
         * カレンダーの節そのものが出ない。ここで既定のURLへ倒すと、
         * 2026-08-27 まで起きていた漏れが戻る。
         */
        this.calendarEmbedUrl = data.dashboardCalendarEmbedUrl || ''
        /* 締めも同じ。**読めなければ、期限の枠を出さない。** */
        this.submissionDueKind = data.submissionDueKind || ''
        this.submissionDueDay = data.submissionDueDay || null
        this.submissionDueItems = data.submissionDueItems || ''
        /* お知らせも同じ。**読めなければ、カードごと出さない。** */
        this.notices = Array.isArray(data.notices) ? data.notices : []
      }).catch(error => console.error('ダッシュボード情報取得失敗:', safeErrorDigest(error)))
    },
    /**
     * お知らせの日付。
     *
     * **読めない値のときは何も出さない。**「Invalid date」と出すくらいなら
     * 日付の欄を空けたほうがよい。
     */
    noticeDate (notice) {
      const at = moment(notice && notice.publishedAt)
      return at.isValid() ? at.format('YYYY.MM.DD') : ''
    },

    /**
     * 見た目の色分け。**知らない種類は system 扱いにする。**
     *
     * ここで種類の名前をそのまま class へ入れると、増えた種類の色が
     * 決まっていないぶんだけ素の見た目になる。
     */
    noticeTone (notice) {
      return (notice && notice.category) === 'UPDATE' ? 'update' : 'system'
    },

    /*
     * 今日の勤怠状況。**権限が無ければ問い合わせもしない。**
     *
     * 呼んで403を受けると、画面には出ているのに中身が出ない状態になり、
     * 利用者からは壊れて見える（打刻カードで同じことが起きている）。
     * /access-context の permissions を先に見て、あるときだけ出す。
     *
     * 稼働状況分析（WORKFORCE_VIEW）より狭く、経営管理者以上に限る。
     */
    /*
     * offset は画面の「次へ」から来る。retry のときは undefined なので
     * 先頭に戻る。1000人規模でも1枚のカードに出し切らないための仕組み。
     */
    fetchAttendanceOverview (offset) {
      // 古い応答が新しい条件の一覧を上書きしないよう、連番で見分ける。
      const requestId = this.attendanceOverviewRequestId + 1
      this.attendanceOverviewRequestId = requestId

      this.attendanceOverviewLoading = true
      this.attendanceOverviewError = false
      const params = {}
      if (Number.isFinite(offset) && offset > 0) {
        params.offset = offset
      }
      /*
       * **絞り込みはサーバへ渡す。**画面で絞ると、ページに載っている
       * 100名だけが対象になり、別のページにいる人を探せない。
       * 月次締めのコックピットがその作りになっている。
       */
      if (this.attendanceOverviewKeyword) {
        params.keyword = this.attendanceOverviewKeyword
      }
      if (this.attendanceOverviewStatus) {
        params.status = this.attendanceOverviewStatus
      }
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/management/attendance-overview', { params })
        .then(response => {
          if (requestId !== this.attendanceOverviewRequestId) return
          this.attendanceOverview = response.data || {}
          this.attendanceOverviewError = false
        })
        .catch(error => {
          if (requestId !== this.attendanceOverviewRequestId) return
          this.attendanceOverviewError = true
          console.error('今日の勤怠状況の取得に失敗しました:', safeErrorDigest(error))
        })
        .finally(() => {
          if (requestId === this.attendanceOverviewRequestId) {
            this.attendanceOverviewLoading = false
          }
        })
    },

    /*
     * 絞り込みを変えたら先頭のページから取り直す。
     *
     * offset を持ったままだと、3ページ目を見ている状態で絞った結果が
     * 1ページしか無いときに空で表示される。
     */
    applyAttendanceOverviewFilter (condition) {
      this.attendanceOverviewKeyword = (condition && condition.keyword) || ''
      this.attendanceOverviewStatus = (condition && condition.status) || ''
      return this.fetchAttendanceOverview()
    },

    /*
     * 月次締めの絞り込み。offset を渡さないので先頭のページから取り直す。
     *
     * offset を持ったままだと、3ページ目を見ている状態で絞った結果が
     * 1ページしか無いときに空で表示される。
     */
    applyCloseCondition (condition) {
      this.closeQuery = (condition && condition.query) || ''
      this.closeState = (condition && condition.state) || ''
      return this.fetchDashboardInsights()
    },

    /** 見せてよい相手か。permissions を正本にする（表示名では判断しない）。 */
    resolveAttendanceOverviewView () {
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/access-context')
        .then(response => {
          const context = response.data || {}
          // よく使うメニュー・月次提出状況・カードのリンクを、左メニューと同じ判定で絞る。
          this.accessContext = context
          const permissions = Array.isArray(context.permissions) ? context.permissions : []
          const features = Array.isArray(context.features) ? context.features : []
          this.attendanceOverviewView =
            permissions.includes('MANAGEMENT_INSIGHT_VIEW') &&
            features.includes('MANAGEMENT_INSIGHT')
          /*
           * 36協定の上限は、稼働状況分析と同じ狭さに加えて
           * **労務法令の判定を出してよい段階か**でも決まる。
           * **同じ1回の応答から決める。**別に /access-context を呼ぶと、
           * ダッシュボードを開くたびに同じ問い合わせが2回飛ぶ。
           *
           * 出さない段階では**問い合わせ自体を送らない。**
           * 送ったうえで空にすると、開くたびに全社ぶんの判定が走る
           * ——非表示なのではなく、計算して捨てていることになる。
           */
          this.overtimeLimitView = this.attendanceOverviewView
            && features.includes('OCCUPATIONAL_LEGAL_COMPLIANCE')
          const pending = []
          if (this.attendanceOverviewView) {
            pending.push(this.fetchAttendanceOverview())
          }
          if (this.overtimeLimitView) {
            pending.push(this.fetchOvertimeLimitStatus())
          }
          return pending.length ? Promise.all(pending) : null
        })
        .catch(error => {
          // 取れなければ出さない。**出したうえで空にするより、出さない方がよい。**
          this.accessContext = null
          this.attendanceOverviewView = false
          this.overtimeLimitView = false
          console.error('権限情報の取得に失敗しました:', safeErrorDigest(error))
        })
    },

    /**
     * 36協定アラート。**専用画面と同じ口を1回だけ呼ぶ。**
     *
     * ダッシュボードに出すのは人数と上位3名だけなので pageSize=3。
     * 対象月は**専用画面と同じ既定（前月）**を使う。別々に決めると、
     * カードの人数と専用画面の人数が食い違う。
     * 失敗したらカードを出さない。**空のカードを出すより、出さない方がよい。**
     */
    fetchOvertimeLimitStatus () {
      const condition = overtimeLimitConditionParams(defaultOvertimeLimitMonth())
      if (!condition) return Promise.resolve()

      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/management/overtime-limit-status', {
          params: { ...condition, currentPage: 1, pageSize: 3 }
        })
        .then(response => {
          this.overtimeLimitStatus = response.data || {}
        })
        .catch(error => {
          this.overtimeLimitStatus = {}
          console.error('36協定アラートの取得に失敗しました:', safeErrorDigest(error))
        })
    },

    /*
     * closeOffset は月次締めの「次へ」から来る。retry のときは undefined
     * なので先頭へ戻る。締め一覧は1000人規模でもページで開けるようにする。
     */
    fetchDashboardInsights (closeOffset) {
      /*
       * 以前はここで「読み込み中なら何もしない」としていた。条件を変えた
       * 直後は前の要求が飛んでいるので、**新しい条件が投げられないまま
       * 古い一覧が残る**。投げたうえで、古い応答を捨てる形にする。
       */
      const requestId = this.insightRequestId + 1
      this.insightRequestId = requestId

      this.insightLoading = true
      this.insightError = false
      const params = {}
      if (Number.isFinite(closeOffset) && closeOffset > 0) {
        params.closeOffset = closeOffset
      }
      // 最後に取りに行った、月次締めのページの位置（先頭なら 0）。
      // 開いたときの勤務表の流しのあとの取り直し（refreshAfterOpenSync）が、同じページを取り直すのに使う。
      this.insightCloseOffset = params.closeOffset || 0
      /*
       * **月次締めの検索・絞り込みはサーバへ渡す。**
       * 画面で絞ると、載っている20人だけが対象になり、
       * 2ページ目にいる人を探せない。件数は全員から数えているので
       * 母数も食い違う。今日の勤怠状況と同じ作りに揃えた。
       */
      if (this.closeQuery) {
        params.closeQuery = this.closeQuery
      }
      if (this.closeState) {
        params.closeState = this.closeState
      }

      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/dashboard/insights', { params })
        .then(response => {
          if (requestId !== this.insightRequestId) return
          const data = response.data || {}
          this.authorityName = String(data.authorityName || this.authorityName || '').trim()
          /*
           * **管理者向けのカードは、サーバの managementView だけで決める。**
           * 以前は旧区分（admin・clerk）でも出していたので、中身を返されない
           * 社内システム管理者に「対象者0名」「承認待ちはありません」の2枚が
           * 出ていた（束34）。
           */
          this.managementView = data.managementView === true
          /*
           * 承認滞留分析は、締めのカードを出す人に加えて、自分宛ての承認を扱える人にも出す
           * （サーバの approvalAgingView）。締めのカードを出す人には、この印に関係なく出す
           * （段の条件が managementView も見る。サーバがこの印をまだ返さない間も、今までどおり）。
           */
          this.approvalAgingView = data.approvalAgingView === true
          this.billingStatus = data.billingStatus || null
          this.actionItems = Array.isArray(data.actionItems) ? data.actionItems : []
          this.workProjection = data.workProjection || {}
          this.closeCockpit = data.closeCockpit || {}
          this.approvalAging = data.approvalAging || {}
          this.insightError = false
        })
        .catch(error => {
          if (requestId !== this.insightRequestId) return
          this.insightError = true
          console.error('ダッシュボード判断支援情報取得失敗:', safeErrorDigest(error))
        })
        .finally(() => {
          if (requestId === this.insightRequestId) this.insightLoading = false
        })
    },
    fetchProjectBreakSetting () {
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/stamping/initial')
        .then(response => { this.useProjectBreakFlg = response.data === true })
        .catch(error => console.error('休憩自動設定フラグ取得失敗:', safeErrorDigest(error)))
    },
    /**
     * 打刻の状態を取る。
     *
     * 取れなかったら、打刻のカードを「取得できませんでした」にして、ボタンを止める（今までどおり）。
     * 状態が分からないまま押させないため。
     *
     * `keepOnFailure` を true で渡したときだけ、**前に取れていた状態があれば、取れなくても残す**。
     * 開いたときの勤務表の計算のあとの取り直し（refreshAfterOpenSync）が使う。この取り直しは、利用者が
     * 何も押していないときに走る。そこで1回取れなかっただけで、使えていた打刻のカードを止めない
     * （出ている値は取り直す前のもの。次に開いたとき・打刻したときに、新しい値が出る）。
     * 画面のボタン（［再取得］）から呼ばれたときは、引数に操作の出来事が入るので、今までどおりに動く。
     */
    fetchStampingStatus (options) {
      const keepOnFailure = !!options && options.keepOnFailure === true && this.stampingStatusLoaded === true
      this.stampingStatusLoading = true
      this.stampingStatusError = false
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/stamping/status')
        .then(response => {
          const status = response.data || {}
          this.attendanceStatus = status.status || 'NOT_STARTED'
          this.businessDate = status.businessDate || ''
          this.startTime = status.startTime || ''
          this.endTime = status.endTime || ''
          this.breakStartedAt = status.breakStartedAt || ''
          this.breakTime = status.breakTime || '00:00'
          this.nightRest = normalizeTimeValue(
            status.nightRest,
            '00:00'
          )
          this.stampingStatusLoaded = true
          this.stampingStatusError = false
        })
        .catch(error => {
          if (!keepOnFailure) {
            this.stampingStatusLoaded = false
            this.stampingStatusError = true
          }
          console.error('打刻状態取得失敗:', safeErrorDigest(error))
        })
        .finally(() => { this.stampingStatusLoading = false })
    },
    formatBusinessDate (value) {
      const date = moment(value, 'YYYY-MM-DD', true)
      return date.isValid() ? date.format('YYYY/MM/DD') : value
    },
    /**
     * 会社が「打刻時の位置情報記録」を入れているかを読む。
     *
     * 打刻の状態の口とは別に読む。**読めなければ、位置を取らずに今までどおり打刻する。**
     * 打刻のカードには響かせない。
     */
    fetchStampLocationSetting () {
      const today = new Date()
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/stamping/events', {
        params: { year: today.getFullYear(), month: today.getMonth() + 1 }
      })
        .then(response => {
          this.stampLocationEnabled =
            !!response && !!response.data && response.data.location_enabled === true
          this.stampRestrictEnabled =
            this.stampLocationEnabled && response.data.restrict_enabled === true
        })
        .catch(error => {
          this.stampLocationEnabled = false
          this.stampRestrictEnabled = false
          console.error('打刻の位置情報の設定の取得失敗:', safeErrorDigest(error))
        })
    },
    handleTime (type) {
      if (this.submitFlg || this.loading || this.stampingStatusLoading) return

      const replacingScheduled = type === 'start' && this.attendanceStatus === 'SCHEDULED'
      if (replacingScheduled && !window.confirm(
        '勤務表に保存済みの定時候補を取り消し、現在時刻を出勤実績として記録します。よろしいですか？'
      )) return

      /*
       * 打刻の時刻は、**押した瞬間の時計**から取る。画面の時計（this.now。1秒ごとに進める値）からは取らない。
       * ブラウザは、裏に回したタブのタイマーを止める・間引く。戻ってすぐ押すと、画面の時計はまだ古く、
       * 数分前の時刻で打刻されてしまう（サーバは、画面から届いた時刻をそのまま使う）。
       * 画面の時計も、同じ時刻へ進めておく。
       *
       * **確認の小窓が出るとき（定時候補を取り消して出勤する）は、確認で［OK］を押した瞬間。**
       * 小窓は「現在時刻を出勤実績として記録します」と言っている。小窓を開いたまま待った人の出勤が、
       * ボタンを押した時刻（小窓を開く前）で記録されないよう、時刻は確認のあとで取る。
       * 確認が無い打刻は、ボタンを押した瞬間のまま（ここまでに待つものが無い）。
       */
      const pressedAt = new Date()
      this.now = pressedAt

      const payload = {
        button_type: type,
        timestamp: formatStampingTimestamp(pressedAt),
        replace_scheduled: replacingScheduled
      }
      /*
       * 会社が「打刻時の位置情報記録」を入れているときだけ、端末の位置を取って一緒に送る。
       * **打刻の時刻は、上で取った「押した瞬間」のまま。**位置を待った分だけ遅い時刻で
       * 打刻されないようにする。位置が取れなくても打刻は送る（取れなかった理由を送る）。
       */
      const withLocation = this.stampLocationEnabled
        ? currentStampingLocation().then(location => Object.assign(payload, location))
        : Promise.resolve()
      /*
       * 退勤のあとに流す月。**押す前の勤務日の月**（日をまたぐ勤務は、出勤した日の月）。
       * 出勤・休憩開始・休憩終了では流さない（終了が無い日は、日ごとの値が決まらない）。
       */
      const rosterMonth = this.rosterMonthAfterStamping(type)
      this.submitFlg = true
      this.loading = true
      // これより前に始めた「黙って流す」分の結果は、画面へ反映しない（打刻の前の状態で流したものなので）。
      this.rosterSyncGeneration += 1
      withLocation.then(() => axios.post(process.env.VUE_APP_API_ORIGIN + '/stamping/update', payload))
        /*
         * **打刻はここで確定している。**続けて、その月の勤務表を、勤務表の画面で
         * ［表示］→［保存］したのと同じ道で流す。流す処理は失敗を投げない
         * （下の打刻失敗の catch へ落とさない）。保存できなかったら、打刻のカードの中へ出す。
         */
        .then(() => this.syncRosterAfterStamping(rosterMonth))
        .then(() => Promise.all([
          this.fetchStampingStatus(),
          this.fetchDashboard(),
          this.fetchDashboardInsights()
        ]))
        .then(() => { alert(ConstList.SUCCESS_STAMPING) })
        .catch(error => {
          console.error('打刻送信失敗:', safeErrorDigest(error))
          alert(backendErrorMessage(error, ConstList.SYSTEM_COMMUNICATION_ERROR))
          return this.fetchStampingStatus()
        })
        .finally(() => { this.submitFlg = false; this.loading = false })
    },

    /**
     * 会社が「定時後の猶予時間を休憩として扱う」を有効にしているかを読む。
     *
     * 勤務表が開くときに読むのと同じ2つの口（本人の所属の案件 → その案件の基準）から取る。
     * 基準に入っているこの設定は会社のもので、どの案件でも同じ値。
     * **読めなければ、今までの表示のまま。**打刻のカードには響かせない。
     */
    fetchPostWorkBreakSetting () {
      return Promise.resolve()
        .then(() => axios.get(process.env.VUE_APP_API_ORIGIN + '/projects'))
        .then(response => {
          const projects = Array.isArray(response && response.data) ? response.data : []
          const first = projects.find(project => project && project.id != null)
          if (!first) return null
          const id = String(first.id)
          return axios
            .get(process.env.VUE_APP_API_ORIGIN + '/project-basics', { params: { ids: id } })
            .then(basics => {
              const basic = basics && basics.data ? basics.data[id] : null
              this.postWorkBreakFlg = !!basic && basic.postWorkBreakFlg === true
            })
        })
        .catch(error => console.error('定時後休憩の設定の取得失敗:', safeErrorDigest(error)))
    },

    /**
     * いま、勤務表を流してよい状態か。
     *
     * **打刻の状態が「定時候補あり」のときは流さない。**流すと当日の行が保存され、
     * 出勤が押せなくなる。状態が取れていないときも、定時候補かどうか分からないので流さない。
     */
    canSyncRoster () {
      return this.stampingStatusLoaded === true && this.attendanceStatus !== 'SCHEDULED'
    },

    /** 打刻のあとに流す月（「2026-09」の形）。流さないなら空文字。 */
    rosterMonthAfterStamping (type) {
      if (type !== 'end') return ''
      if (!this.canSyncRoster()) return ''
      const target = rosterMonthOf(this.businessDate)
      return target ? target.key : ''
    },

    /**
     * 画面に出さずに動かす勤務表の本体。**要るときにだけ読み込む**
     * （ダッシュボードの最初の読み込みを重くしない）。
     */
    loadRosterHeadlessSync () {
      return import(/* webpackChunkName: "roster-headless-sync" */ '@/report/rosterHeadlessSync')
        .then(module => module.rosterHeadlessSync)
    },

    /**
     * 1か月ぶんを流す。
     *
     * **失敗を投げない。**結果は値で返す（年月が読めなければ null）。
     * 部品を読み込めない・思わぬ失敗・時間切れも、結果の1つとして返す。
     *
     * **打ち切ったあとは、本体を保存へ進ませない。**本体は順番に1つずつ流すので、前の回が
     * 詰まっていると、こちらの外枠の時間切れが先に来ることがある。「保存できなかった」と
     * 出したあとで本体が裏で保存まで進むと、知らせと実際が食い違う。本体へ「もう打ち切られたか」を
     * 答える口（isCancelled）を渡す。打ち切るのは、外枠の時間切れと、画面を離れたとき。
     * 保存を送ったあとは止められない（そのときの扱いは今までと同じ）。
     *
     * **打刻が始まっただけでは打ち切らない。**開いたときに流している最中の月は、そのまま
     * 保存まで進ませる（結果は画面へ出さない）。打ち切ると、開いてすぐ［出勤］を押す人は、
     * 前の日の計算がいつまでも記録されない。
     */
    runRosterSync (yearMonth) {
      const target = rosterMonthOf(yearMonth)
      if (!target) return Promise.resolve(null)

      const failed = stage => ({ ok: false, stage, reasons: [] })
      let abandoned = false
      const isCancelled = () => abandoned || this.rosterSyncLeft === true
      let timer = null
      const timedOut = new Promise(resolve => {
        timer = window.setTimeout(
          () => {
            abandoned = true
            resolve(failed('timeout'))
          },
          ROSTER_SYNC_TIMEOUT_MS + ROSTER_SYNC_OUTER_GRACE_MS
        )
      })
      const finished = Promise.resolve()
        .then(() => this.loadRosterHeadlessSync())
        .then(sync => sync({
          year: target.year,
          month: target.month,
          store: this.$store,
          timeoutMs: ROSTER_SYNC_TIMEOUT_MS,
          isCancelled
        }))
        .catch(error => {
          console.error('勤務表の計算の記録に失敗:', safeErrorDigest(error))
          return failed('error')
        })

      return Promise.race([finished, timedOut]).then(result => {
        window.clearTimeout(timer)
        return { ...result, key: target.key, label: target.label }
      })
    },

    /**
     * 1か月ぶんを流す。ほかで先に保存されて断られたときは、月を取り直して、**もう1回だけ**流す。
     *
     * 断られた直後に「勤務表を開いて［保存］してください」と出すと、利用者が何もしなくても
     * 直る止まり方で、利用者を勤務表へ行かせてしまう。知らせを出す前に、計算が済んでいない月を
     * 取り直す。
     *
     * - その月がまだ残っている → もう1回だけ流し、その結果を返す（また断られたら、その結果で知らせが出る）。
     * - その月が残っていない → ほかの保存で、計算はもう記録されている。流し直さず、
     *   「ほかで記録された」という印（settledElsewhere）を付けて返す（知らせは出さない）。
     * - 月を取り直せない（口が落ちた・応答が読めない・時間内に返ってこない）→ 流し直さず、
     *   最初の結果を返す（知らせを出す側）。
     *
     * 流し直すのは1回だけ。ここは自分を呼び直さない（回り続けない）。**失敗を投げない。**
     * **取り直しにも時間切れを置く**（流す本体の時間切れの外なので、返事が来ないと「送信中」が終わらない）。
     * 切れたあとで返事が届いても、もう使わない（流し直さない）。
     *
     * @param stillWanted 流し直す前に、まだ流してよいかを答える関数。開いたときに流す分は、
     *   途中で打刻が始まったら流し直さない。画面を離れたあとも流し直さない。
     */
    runRosterSyncRetryingConflict (yearMonth, stillWanted = () => true) {
      return this.runRosterSync(yearMonth).then(first => {
        if (!isRosterSyncConflict(first)) return first
        const wanted = () => this.rosterSyncLeft !== true && stillWanted() === true
        if (!wanted()) return first
        let timer = null
        // 時間内に返ってこなければ、応答なし（null）として先へ進む。
        const timedOut = new Promise(resolve => {
          timer = window.setTimeout(() => resolve(null), ROSTER_SYNC_RECHECK_TIMEOUT_MS)
        })
        const asked = Promise.resolve()
          .then(() => axios.get(process.env.VUE_APP_API_ORIGIN + '/stamping/pendingRosterMonths'))
        return Promise.race([asked, timedOut])
          .then(response => {
            window.clearTimeout(timer)
            const months = response && response.data && Array.isArray(response.data.months)
              ? response.data.months
              : null
            if (!months) return first
            const remains = months.some(value => {
              const target = rosterMonthOf(value)
              return !!target && target.key === first.key
            })
            if (!remains) return { ...first, settledElsewhere: true }
            if (!wanted()) return first
            return this.runRosterSync(first.key)
          })
          .catch(error => {
            window.clearTimeout(timer)
            console.error('勤務表の計算が済んでいない月の取得失敗:', safeErrorDigest(error))
            return first
          })
      })
    },

    /**
     * 流した結果を、打刻のカードへ反映する。
     *
     * - 保存できた月は、出ていた知らせを消す。ほかの保存で計算が記録された月も、同じく消す。
     * - 保存できなかった月は、知らせを出す（同じ月が出ていたら入れ替える）。
     * - 勤務表を開いて［保存］しても直らない止まり方は、何も変えない。
     *
     * @param result        runRosterSync が返した値
     * @param afterClockOut この画面で押した退勤のあとに流した分なら true（開いたときに流した分は false）。
     *   太字の1行の出し分けに使う。退勤のあとに出た月は、開いたときの流しでもう1度
     *   保存できなくても、退勤のあとの月のまま（この画面で押した退勤の月が残っているあいだは退勤の版）。
     */
    applyRosterSyncResult (result, afterClockOut) {
      if (!result || !result.key) return
      const others = this.rosterSyncFailures.filter(item => item.key !== result.key)
      if (result.ok === true || result.settledElsewhere === true) {
        this.rosterSyncFailures = others
        return
      }
      if (ROSTER_SYNC_SILENT_STAGES.includes(result.stage)) return
      const shown = this.rosterSyncFailures.find(item => item.key === result.key)
      this.rosterSyncFailures = others
        .concat([{
          key: result.key,
          label: result.label,
          reasons: rosterSyncReasons(result),
          afterClockOut: afterClockOut === true || (!!shown && shown.afterClockOut === true)
        }])
        .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
    },

    /**
     * 退勤の打刻のあと、その月を流す。**打刻の失敗にしない**（必ず resolve する）。
     */
    syncRosterAfterStamping (yearMonth) {
      if (!yearMonth) return Promise.resolve()
      return this.runRosterSyncRetryingConflict(yearMonth)
        .then(result => this.applyRosterSyncResult(result, true))
        .catch(error => console.error('勤務表の計算の記録に失敗:', safeErrorDigest(error)))
    },

    /**
     * ダッシュボードを開いたとき、打刻したのに勤務表の計算が済んでいない月を、黙って流す。
     *
     * - **月を取る口が落ちても、打刻のカードには響かせない**（状態の取得ともボタンとも別）。
     * - 1回の表示で、同じ月は1度だけ。
     * - 途中で打刻が始まったら、残りの月は流さない。結果も画面へ反映しない
     *   （打刻の前の状態で流したものなので。流れなかった月は、次に開いたときに拾う）。
     *   流している最中の月は、止めない（runRosterSync の説明を参照）。
     * - **1か月でも保存できたら、流し終えたあとに、打刻のカードの値を取り直す**（refreshAfterOpenSync）。
     */
    syncPendingRosterMonths () {
      if (!this.canSyncRoster()) return Promise.resolve()
      const generation = this.rosterSyncGeneration
      // この回の流しで、保存できた月があったか。
      let savedAny = false
      return Promise.resolve()
        .then(() => axios.get(process.env.VUE_APP_API_ORIGIN + '/stamping/pendingRosterMonths'))
        .then(response => {
          const months = response && response.data && Array.isArray(response.data.months)
            ? response.data.months
            : []
          return months.reduce((chain, value) => chain.then(() => {
            const target = rosterMonthOf(value)
            if (!target) return null
            if (generation !== this.rosterSyncGeneration) return null
            if (this.rosterSyncTriedMonths[target.key]) return null
            this.rosterSyncTriedMonths[target.key] = true
            const stillWanted = () => generation === this.rosterSyncGeneration
            return this.runRosterSyncRetryingConflict(target.key, stillWanted).then(result => {
              if (result && result.ok === true) savedAny = true
              if (generation === this.rosterSyncGeneration) this.applyRosterSyncResult(result, false)
            })
          }), Promise.resolve())
        })
        .catch(error => console.error('勤務表の計算が済んでいない月の取得失敗:', safeErrorDigest(error)))
        .then(() => this.refreshAfterOpenSync(savedAny, generation))
    },

    /**
     * 開いたときの流しで保存できたら、打刻のカードの値を取り直す。
     *
     * 流して保存すると、その日の休憩が変わることがある（定時後の休憩が足される）。打刻のカードの
     * 「休憩合計」は、開いたときに取った値のままなので、取り直さないと、読み込み直すまで古い値が出る。
     * 退勤のあとと同じ取り直し（打刻の状態・サマリー・着地見込み）を、1回だけ行う。
     *
     * - 保存できた月が無ければ、取り直さない（何も変わっていない）。
     * - 流しているあいだに打刻が始まっていたら、取り直さない（打刻の側が、自分のあとで取り直す。
     *   二重に走らせない）。画面を離れたあとも取り直さない。
     * - 取り直しの失敗で、知らせは増やさない。**失敗を投げない。**
     * - **打刻の状態が取れなくても、前に取れていた状態を残す。**使えていた打刻のカードを
     *   「取得できませんでした」にしない（ボタンを止めない）。次に開いたときに、新しい値が出る。
     * - **月次締めの一覧は、いま見ているページのまま取り直す。**この取り直しは、利用者が何も押していない
     *   ときに走る。開いてすぐ「次へ」を押した人の一覧を、先頭のページへ戻さない
     *   （退勤のあとの取り直しは、自分で押した直後なので、今までどおり先頭から）。
     *
     * @param savedAny   この回の流しで、保存できた月があったか
     * @param generation この回の流しを始めたときの番号（打刻が始まると進む）
     */
    refreshAfterOpenSync (savedAny, generation) {
      if (savedAny !== true) return Promise.resolve()
      if (generation !== this.rosterSyncGeneration || this.rosterSyncLeft === true) return Promise.resolve()
      return Promise.all([
        this.fetchStampingStatus({ keepOnFailure: true }),
        this.fetchDashboard(),
        this.fetchDashboardInsights(this.insightCloseOffset)
      ])
        .then(() => undefined)
        .catch(error => console.error('打刻のカードの取り直しに失敗:', safeErrorDigest(error)))
    }
  }
}
</script>

<style scoped>
.preika-dashboard{--preika-primary:#0a66b2;--preika-navy:#0c2642;--preika-ink:#172b3f;--preika-muted:#6f8193;--preika-border:#e5edf4;padding:10px 4px 36px;color:var(--preika-ink)}
.hero-panel{position:relative;display:flex;min-height:320px;margin-bottom:24px;overflow:hidden;border-radius:24px;background:linear-gradient(120deg,#064f91 0%,#0b70bc 48%,#0a5ca4 100%);box-shadow:0 24px 60px rgba(5,66,118,.24)}
.hero-panel__content{position:relative;z-index:3;width:60%;padding:42px 52px}.hero-brand{display:flex;align-items:center;margin-bottom:28px}.hero-brand__logo{width:150px;max-height:42px;margin-right:16px;object-fit:contain;filter:brightness(0) invert(1)}.hero-brand__badge{padding:6px 10px;border:1px solid rgba(255,255,255,.26);border-radius:99px;color:rgba(255,255,255,.8);font-size:12px;font-weight:700;letter-spacing:.13em}.hero-panel__eyebrow{margin:0 0 10px;color:#a9dcff;font-size:14px;font-weight:700}.hero-panel__title{margin:0;color:#fff;font-size:38px;font-weight:700;line-height:1.28}.hero-panel__title span{color:#d9f1ff}.hero-panel__description{max-width:580px;margin:18px 0 24px;color:rgba(255,255,255,.78);font-size:14px;line-height:1.9}.hero-panel__meta{display:flex;gap:12px}.hero-meta-item{display:inline-flex;align-items:center;gap:8px;padding:9px 14px;border:1px solid rgba(255,255,255,.15);border-radius:10px;background:rgba(255,255,255,.08);color:#fff;font-size:13px}
.hero-visual{position:relative;z-index:2;flex:1}.hero-visual__orbit{position:absolute;border:1px solid rgba(255,255,255,.13);border-radius:50%}.hero-visual__orbit--one{top:35px;right:18px;width:290px;height:290px}.hero-visual__orbit--two{top:82px;right:62px;width:200px;height:200px}.hero-visual__card{position:absolute;border:1px solid rgba(255,255,255,.28);background:rgba(255,255,255,.94);box-shadow:0 28px 55px rgba(0,27,59,.28)}.hero-visual__card--rear{top:58px;right:32px;width:230px;height:145px;padding:28px 24px;border-radius:18px;transform:rotate(8deg);opacity:.8}.hero-visual__card--front{top:87px;right:110px;width:245px;height:190px;padding:20px;border-radius:20px;transform:rotate(-4deg)}.visual-line{display:block;width:100%;height:9px;margin-bottom:14px;border-radius:20px;background:#dce8f1}.visual-line--short{width:34%;background:#83c9f4}.visual-line--middle{width:72%}.visual-card__header{display:flex;align-items:center;justify-content:space-between}.visual-avatar{display:inline-flex;width:38px;height:38px;align-items:center;justify-content:center;border-radius:12px;background:#0871be;color:#fff;font-weight:800}.visual-status{width:62px;height:8px;border-radius:20px;background:#dfeaf2}.visual-chart{display:flex;height:85px;align-items:flex-end;gap:10px;margin-top:20px}.visual-chart span{flex:1;border-radius:7px 7px 3px 3px;background:linear-gradient(180deg,#43b8ee,#0871be)}.visual-card__footer{display:flex;gap:8px;margin-top:14px}.visual-card__footer span{width:42px;height:7px;border-radius:10px;background:#dfe9f1}
.dashboard-grid{display:grid;gap:24px;margin-bottom:24px}.dashboard-grid--top{grid-template-columns:minmax(0,1.12fr) minmax(350px,.88fr)}.dashboard-grid--overview{grid-template-columns:minmax(0,1.08fr) minmax(350px,.92fr)}.dashboard-grid--middle{grid-template-columns:minmax(0,1.06fr) minmax(340px,.94fr)}.dashboard-grid--single{grid-template-columns:1fr}.dashboard-card{overflow:hidden;border:1px solid var(--preika-border);border-radius:20px;background:#fff;box-shadow:0 12px 32px rgba(19,54,83,.07)}.attendance-card,.quick-card,.notice-card,.submission-card,.monthly-summary-card,.monthly-status-card{padding:28px}.card-heading{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:22px}.card-heading__eyebrow{margin:0 0 4px;color:var(--preika-primary);font-size:12px;font-weight:800;letter-spacing:.16em}.card-heading h2{margin:0;font-size:21px}.card-heading__icon{width:24px;color:#92a9ba}
.status-pill{display:inline-flex;align-items:center;gap:7px;padding:7px 11px;border-radius:99px;background:#f1f5f8;color:#7b8d9b;font-size:12px;font-weight:700}.status-pill__dot{width:7px;height:7px;border-radius:50%;background:#aebbc5}.status-pill--active{background:#e9f7f1;color:#148055}.status-pill--active .status-pill__dot{background:#24ad77}.status-pill--break{background:#fff2e5;color:#b9630c}.status-pill--break .status-pill__dot{background:#e58a27}.status-pill--error{background:#fdeaea;color:#ad3f3f}.status-pill--error .status-pill__dot{background:#d95a5a}.attendance-time{display:flex;align-items:baseline;gap:16px;margin-bottom:22px}.attendance-time strong{color:var(--preika-navy);font-size:38px}.attendance-time span{color:var(--preika-muted);font-size:13px}.attendance-actions{display:grid;grid-template-columns:1fr 1fr;gap:12px}.attendance-button{display:flex;min-height:76px;align-items:center;gap:14px;padding:13px 15px;border:1px solid #e2eaf0;border-radius:14px;background:#fff;text-align:left;transition:.2s}.attendance-button:not(:disabled):hover{border-color:#a8d5ef;box-shadow:0 10px 24px rgba(10,102,178,.12);transform:translateY(-2px)}.attendance-button:disabled{opacity:.5}.attendance-button__icon{display:inline-flex;width:42px;height:42px;align-items:center;justify-content:center;border-radius:12px}.attendance-button strong,.attendance-button small{display:block}.attendance-button small{color:#8293a1;font-size:12px}.attendance-button__phrase{display:inline-block;max-width:100%}.attendance-button--start .attendance-button__icon{background:#e5f6ef;color:#159565}.attendance-button--end .attendance-button__icon{background:#e8f2fb;color:#0a66b2}.attendance-button--break .attendance-button__icon{background:#fff2e5;color:#d97a16}.attendance-button--return .attendance-button__icon{background:#eeeafc;color:#6550b9}.attendance-stamp-result{margin-top:18px;padding:15px;border:1px solid #e2ebf2;border-radius:15px;background:linear-gradient(180deg,#fbfdff,#f5f9fc)}.attendance-stamp-result__header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:11px}.attendance-stamp-result__header p{margin:0 0 2px;color:#0a66b2;font-size:12px;font-weight:800;letter-spacing:.14em}.attendance-stamp-result__header h3{margin:0;color:#0c2642;font-size:14px}.attendance-stamp-result__date{padding:5px 8px;border-radius:99px;background:#eaf3fa;color:#527188;font-size:12px;font-weight:700}.attendance-stamp-result__grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px}.attendance-stamp-result__phrase{display:inline-block;max-width:100%}.attendance-stamp-result__item{position:relative;min-width:0;padding:11px 10px;border:1px solid #e4ebf1;border-radius:11px;background:#fff}.attendance-stamp-result__item::before{position:absolute;top:0;right:10px;left:10px;height:3px;border-radius:0 0 4px 4px;content:''}.attendance-stamp-result__item--start::before{background:#24ad77}.attendance-stamp-result__item--end::before{background:#0a66b2}.attendance-stamp-result__item--break::before{background:#e58a27}.attendance-stamp-result__item--night::before{background:#6550b9}.attendance-stamp-result__label,.attendance-stamp-result__item strong,.attendance-stamp-result__item small{display:block}.attendance-stamp-result__label{margin-top:2px;color:#718594;font-size:12px;font-weight:700}.attendance-stamp-result__item strong{margin:4px 0 1px;overflow:hidden;color:#0c2642;font-size:17px;letter-spacing:.02em;text-overflow:ellipsis;white-space:nowrap}.attendance-stamp-result__item small{color:#93a2ad;font-size:12px}.attendance-status-error{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:18px;padding:14px 15px;border:1px solid #f0cccc;border-radius:13px;background:#fff7f7}.attendance-status-error strong,.attendance-status-error small{display:block}.attendance-status-error strong{color:#9f3939;font-size:12px}.attendance-status-error small{margin-top:3px;color:#9b6a6a;font-size:12px}.attendance-status-error button{padding:7px 11px;border:1px solid #dca8a8;border-radius:9px;background:#fff;color:#a13d3d;font-size:12px;font-weight:700}.attendance-status-error button:disabled{opacity:.5}.attendance-loading{margin-top:16px;color:var(--preika-primary);font-size:12px}
.quick-menu{display:grid;grid-template-columns:repeat(auto-fill,minmax(max(220px,calc((100% - 10px) / 2)),1fr));gap:10px}.quick-menu__item{display:flex;min-height:78px;align-items:center;gap:12px;padding:13px;border:1px solid #e6edf3;border-radius:14px;color:inherit;text-decoration:none}.quick-menu__icon{display:inline-flex;width:40px;height:40px;align-items:center;justify-content:center;border-radius:12px}.quick-menu__icon--blue{background:#e5f2fc;color:#0a66b2}.quick-menu__icon--navy{background:#e9eef4;color:#213e59}.quick-menu__icon--green{background:#e5f6ef;color:#16875f}.quick-menu__icon--orange{background:#fff0df;color:#cc7318}.quick-menu__icon--purple{background:#eee9fb;color:#6b52bd}.quick-menu__icon--cyan{background:#e3f5f7;color:#16808a}.quick-menu__text{flex:1}.quick-menu__text strong,.quick-menu__text small{display:block}.quick-menu__text small{color:#8393a0;font-size:12px}.quick-menu__arrow{width:14px;color:#b0bdc7}
.metric-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;margin-bottom:24px}.metric-card{display:flex;min-height:118px;align-items:center;gap:16px;padding:20px;border:1px solid var(--preika-border);border-radius:18px;background:#fff;color:inherit;box-shadow:0 10px 28px rgba(19,54,83,.06);text-decoration:none}.metric-card__icon{display:inline-flex;width:48px;height:48px;align-items:center;justify-content:center;border-radius:15px}.metric-card--blue .metric-card__icon{background:#e4f2fc;color:#0a66b2}.metric-card--orange .metric-card__icon{background:#fff0df;color:#cf7315}.metric-card--purple .metric-card__icon{background:#eee9fb;color:#6851b7}.metric-card--green .metric-card__icon{background:#e4f6ed;color:#14835b}.metric-card__body small,.metric-card__body strong,.metric-card__body span{display:block}.metric-card__body small{color:#778997;font-size:12px}.metric-card__body strong{margin:3px 0;font-size:28px}.metric-card__body span{color:#95a3ae;font-size:12px}.metric-card__body .metric-card__time{font-size:23px}
.summary-month{padding:6px 10px;border-radius:8px;background:#eef5fa;color:#607789;font-size:12px}.summary-list{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}.summary-item{padding:17px;border-radius:14px;background:#f7f9fb}.summary-item__label{display:block;margin-bottom:8px;color:#788b99;font-size:12px}.summary-item strong{font-size:18px}.text-link{display:inline-flex;align-items:center;gap:6px;color:var(--preika-primary);font-size:12px}.document-status-list{display:grid;gap:9px}.document-status{display:grid;grid-template-columns:38px 1fr auto;align-items:center;gap:12px;padding:12px 14px;border:1px solid #e8eef3;border-radius:13px}.document-status__icon{display:inline-flex;width:36px;height:36px;align-items:center;justify-content:center;border-radius:10px;background:#edf5fa;color:var(--preika-primary)}.document-status__name{font-size:12px;font-weight:700}.document-status__badge{min-width:64px;padding:5px 8px;border-radius:99px;text-align:center;font-size:12px;font-weight:800}.document-status__badge--neutral{background:#f0f3f5;color:#6f7f8b}.document-status__badge--pending{background:#fff3d9;color:#9d6900}.document-status__badge--rejected{background:#fde8e8;color:#b44444}.document-status__badge--approved{background:#e4f6ed;color:#14764f}.document-status__badge--empty{background:#f3f5f7;color:#9aa6ae}
.notice-count{color:#8798a5;font-size:12px}.notice-list{border-top:1px solid #edf2f6}.notice-item{display:grid;grid-template-columns:92px 1fr;gap:18px;padding:17px 2px;border-bottom:1px solid #edf2f6}.notice-item__date{color:#8a9aa7;font-size:12px}.notice-item__content{display:flex;align-items:center;gap:12px}.notice-item__content p{margin:0;font-size:12px}.notice-item__category{padding:4px 8px;border-radius:6px;font-size:12px}.notice-item__category--update{background:#e5f2fc;color:#0a66b2}.notice-item__category--system{background:#fff0df;color:#bc6913}.submission-guide{display:grid;gap:12px}.submission-guide__item{display:flex;align-items:center;gap:16px;padding:17px;border-radius:15px}.submission-guide__item--monthly{background:#eef6fc}.submission-guide__item--anytime{background:#eef8f4}.submission-guide__number{display:inline-flex;width:48px;height:48px;align-items:center;justify-content:center;border-radius:14px;background:#fff;color:var(--preika-primary);font-weight:800}.submission-guide p{margin:0;color:#6f8190;font-size:12px}.submission-link{display:flex;justify-content:space-between;margin-top:17px;padding:12px 15px;border-radius:11px;background:var(--preika-navy);color:#fff;text-decoration:none}.calendar-card__heading{margin:0;padding:25px 28px 20px}.calendar-frame{min-height:520px;border-top:1px solid #e8eef3}.calendar-frame iframe{display:block;width:100%;height:600px;border:0}
@media(max-width:1199px){.hero-panel__content{width:62%;padding:38px}.hero-visual__card--front{right:55px}.dashboard-grid--top,.dashboard-grid--middle,.dashboard-grid--overview{grid-template-columns:1fr}}
@media(max-width:900px){.metric-grid{grid-template-columns:1fr 1fr}}
@media(max-width:767px){.hero-panel{min-height:auto;border-radius:18px}.hero-panel__content{width:100%;padding:30px 24px}.hero-brand{align-items:flex-start;flex-direction:column;gap:10px}.hero-panel__title{font-size:29px}.hero-visual{display:none}.attendance-card,.quick-card,.notice-card,.submission-card,.monthly-summary-card,.monthly-status-card{padding:22px 18px}.attendance-time{align-items:flex-start;flex-direction:column}.attendance-actions,.quick-menu,.metric-grid,.summary-list{grid-template-columns:1fr}.attendance-stamp-result__header{align-items:flex-start;flex-direction:column}.attendance-stamp-result__grid{grid-template-columns:1fr 1fr}.attendance-status-error{align-items:flex-start;flex-direction:column}.attendance-status-error button{width:100%}.notice-item{grid-template-columns:1fr}.notice-item__content{align-items:flex-start;flex-direction:column}}
.status-pill--scheduled{background:#eef1ff;color:#5b57a8}
.dashboard-grid.dashboard-grid--single{grid-template-columns:1fr}
a.document-status{color:inherit;text-decoration:none;transition:border-color .2s}
a.document-status:hover{border-color:#a8d5ef}
.monthly-status-card__month{display:block;margin-top:4px;color:#607789;font-size:12px}
.preika-billing-line{display:flex;flex-wrap:wrap;align-items:center;gap:8px 12px;margin:0 0 24px;padding:14px 18px;border:1px solid #cfe3f3;border-radius:14px;background:#f3f9fe;color:#1c3d5a;font-size:13px}
.preika-billing-line__text{flex:1 1 16em;min-width:0;font-weight:700}
.preika-billing-line__link{display:inline-flex;align-items:center;gap:6px;padding:7px 12px;border:1px solid #0a66b2;border-radius:9px;background:#0a66b2;color:#fff;font-size:12px;font-weight:700}
.status-pill--scheduled .status-pill__dot{background:#746ed0}
.attendance-scheduled-note{margin-bottom:10px;padding:9px 11px;border-radius:9px;background:#eef1ff;color:#55538e;font-size:12px;line-height:1.6}

/* 本文の塊の幅（24em）は、固定休憩の注意の本文が1行に入る幅。太字の横に入らなければ、次の行へ回して全部の幅で出す（「くださ／い。」と最後の字だけが落ちないように）。 */
.attendance-break-note{display:flex;flex-wrap:wrap;align-items:center;gap:4px 8px;margin-top:12px;padding:10px 12px;border:1px solid #f0d7b9;border-radius:11px;background:#fffaf3;color:#80552a;font-size:12px;line-height:1.55}.attendance-break-note strong{flex:0 0 auto;color:#9a5f1f}.attendance-break-note span{min-width:0;flex:1 1 24em;word-break:auto-phrase}

/* 打刻は記録できたが、勤務表の計算を保存できなかったときの知らせ。打刻の失敗（赤）とは色を分ける。 */
.attendance-roster-sync-note{margin-top:12px;padding:11px 13px;border:1px solid #e6d28f;border-radius:11px;background:#fffbe8;color:#5f4c12;font-size:12px;line-height:1.65}
.attendance-roster-sync-note>strong,.attendance-roster-sync-note>span{display:block}
.attendance-roster-sync-note>strong{color:#6f5200}
.attendance-roster-sync-note__month{margin-top:8px;padding-top:8px;border-top:1px solid #efe1ae}
.attendance-roster-sync-note__label{display:block;font-weight:700}
.attendance-roster-sync-note__month ul{max-height:9.9em;margin:4px 0 0;padding-left:1.3em;overflow-y:auto}
.attendance-roster-sync-note__month li{overflow-wrap:anywhere}
</style>
